<DOMContainer width="100%" height="100%">
    <div class="rpg-ui-menu-panel rpg-ui-panel">
        <div class="rpg-ui-menu-panel-header">{t("rpg.menu.items")}</div>
        <div class="rpg-ui-menu-panel-body rpg-ui-menu-panel-body-stacked">
            <div>
                @if (currentItem) {
                    <div class="rpg-ui-menu-panel-details rpg-ui-panel">
                        <div class="rpg-ui-menu-panel-hero">
                            <div class="rpg-ui-menu-panel-hero-icon">
                                @if (currentItem()?.icon) {
                                <DOMSprite 
                                    sheet={iconSheet(currentItem()?.icon)} 
                                    playing="default" 
                                    width="48px"
                                    height="48px"
                                    objectFit="contain"
                                />
                                } @else {
                                    <span class="rpg-ui-fallback-icon" aria-hidden="true">◇</span>
                                }
                            </div>
                            <div>
                                <div class="rpg-ui-menu-panel-details-title">{currentItem()?.name}</div>
                                <div class="rpg-ui-menu-panel-details-desc">{currentItem()?.description || ""}</div>
                                @if (currentItemSlot() >= 0) {
                                    <div class="rpg-item-menu-assigned">
                                        {t("rpg.menu.assigned-slot", { slot: slotLabels[currentItemSlot()] })}
                                    </div>
                                }
                                @if (currentItem()?.type !== "item") {
                                    <div class="rpg-item-menu-equipment-help">
                                        {t("rpg.menu.manage-in-equip")}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            
            <div class="rpg-ui-menu-panel-list rpg-ui-menu rpg-ui-menu-panel-list-full">
                <div class="rpg-ui-menu-tabs">
                    @for ((tab,tabIndex) of tabs) {
                        <div
                            class="rpg-ui-menu-tab"
                            class={{active: selectedTab() === tabIndex}}
                            tabindex={tabIndex}
                            click={selectTab(tabIndex)}
                        >{t(tab.label)}</div>
                    }
                </div>
                <Navigation tabindex={selectedItem} controls={controls}>
                    @if (!filteredItems().length) {
                        <div class="rpg-ui-empty-state"><span aria-hidden="true">◇</span><h3>{t("rpg.menu.no-items")}</h3><p>{t("rpg.menu.no-items-help")}</p></div>
                    }
                    @for ((item,index) of filteredItems) {
                        <div
                            class="rpg-ui-menu-item rpg-ui-menu-row"
                            class={{disabled: item.type === "item" && !item.usable}}
                            data-selected={selectedItem() === index ? "true" : "false"}
                            tabindex={index}
                            click={selectItem(index)}
                        >
                            <div class="rpg-ui-menu-row-main">
                                <div class="rpg-ui-menu-row-icon">
                                    @if (item.icon) {
                                    <DOMSprite 
                                        sheet={iconSheet(item.icon)} 
                                        playing="default" 
                                        width="28px"
                                        height="28px"
                                        objectFit="contain"
                                    />
                                    } @else {
                                        <span class="rpg-ui-fallback-icon" aria-hidden="true">◇</span>
                                    }
                                </div>
                                <span>{item.name}</span>
                            </div>
                            <span class="rpg-ui-menu-row-end">x{item.quantity ?? 1}</span>
                        </div>
                    }
                </Navigation>
            </div>
        </div>
        @if (confirmOpen) {
            <div class="rpg-ui-menu-confirm" click={cancelConfirmFromPointer}>
                <button class="rpg-ui-close-button" type="button" title={t("rpg.ui.close")} aria-label={t("rpg.menu.close-confirmation")} click={cancelConfirmFromPointer}>x</button>
                <div class="rpg-ui-menu-confirm-card" click={stopEvent}>
                    <div class="rpg-ui-menu-confirm-title">{t("rpg.menu.use-item", { name: confirmItem()?.name })}</div>
                    <Navigation tabindex={selectedConfirm} controls={confirmControls}>
                        <div class="rpg-ui-menu-confirm-actions">
                            @for ((option,index) of confirmOptions) {
                                <button
                                    class="rpg-ui-menu-confirm-btn"
                                    class={{"rpg-ui-menu-confirm-btn-secondary": option.id === "cancel"}}
                                    data-selected={selectedConfirm() === index ? "true" : "false"}
                                    tabindex={index}
                                    click={confirmSelect(index)}
                                >{option.label}</button>
                            }
                        </div>
                    </Navigation>
                </div>
            </div>
        }
    </div>
</DOMContainer>

<style>
    .rpg-item-menu-assigned {
        margin-top: 8px;
        color: var(--rpg-ui-accent);
        font-size: 0.76rem;
        font-weight: 800;
    }

    .rpg-item-menu-equipment-help {
        margin-top: 8px;
        color: var(--rpg-ui-text-muted, #b9b2a6);
        font-size: 0.76rem;
        font-weight: 700;
    }
</style>

<script>
    import { signal, computed, createTabindexNavigator, effect } from "canvasengine";
    import { selectionSound } from "../lifecycle";
    import { inject } from "../../../core/inject";
    import { RpgClientEngine } from "../../../RpgClientEngine";
    import { delay } from "@rpgjs/common";
    import { getKeyboardControlBind } from "../../../services/actionInput";
    import { menuGamepadControls } from "../../../services/gamepadControls";

    const engine = inject(RpgClientEngine);
    const { t } = engine.i18n();
    const keyboardControls = engine.globalConfig.keyboardControls;

    const selectedItem = signal(0);
    const selectedTab = signal(0);
    selectionSound(() => `${selectedTab()}:${selectedItem()}`, () => engine.playUiSound("navigate"));
    const selectedConfirm = signal(0);
    const confirmOpen = signal(false);
    const confirmItem = signal(null);

    const { data, assignmentOpen, onAssignHotbar, onInteraction, onBack } = defineProps();

    const items = computed(() => data().items);
    const hotbar = computed(() => data().hotbar || { slots: [] });
    const isAssignmentOpen = () => Boolean(resolveProp(assignmentOpen));
    const canAssignItems = computed(() => {
        const allowed = hotbar().allowedEntryTypes;
        return !Array.isArray(allowed) || allowed.includes("item");
    });
    const slotLabels = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

    const tabs = [
        { id: "item", label: "rpg.menu.items" },
        { id: "weapon", label: "rpg.menu.weapons" },
        { id: "armor", label: "rpg.menu.armor" }
    ];

    const resolveProp = (value) => typeof value === "function" ? value() : value;
    const safeItems = computed(() => {
        const value = resolveProp(items);
        return Array.isArray(value) ? value : [];
    });

    const filteredItems = computed(() => {
        const items = safeItems();
        const tab = tabs[selectedTab()];
        if (!tab) return [];
        return items.filter((item) => item.type === tab.id && (item.quantity ?? 1) > 0);
    });

    const confirmOptions = computed(() => [
        { id: "use", label: t("rpg.menu.use") },
        ...(canAssignItems() && confirmItem()?.hotbarAssignable
            ? [{ id: "assign", label: t("rpg.menu.assign-hotbar") }]
            : []),
        { id: "cancel", label: t("rpg.menu.cancel") }
    ]);

    const nav = createTabindexNavigator(selectedItem, { count: () => filteredItems().length }, "wrap");
    const navConfirm = createTabindexNavigator(selectedConfirm, { count: () => confirmOptions().length }, "wrap");
    const currentItem = computed(() => filteredItems()[selectedItem()]);
    const itemSlot = (item) => {
        if (!item) return -1;
        return (hotbar().slots || []).findIndex(
            (slot) => slot?.entry?.type === "item" && slot.entry.id === item.id
        );
    };
    const currentItemSlot = computed(() => itemSlot(currentItem()));
    const iconSheet = (iconId) => ({
        definition: engine.getSpriteSheet(iconId)
    });

    const clampSelection = () => {
        const count = filteredItems().length;
        if (count === 0) {
            selectedItem.set(0);
            return;
        }
        if (selectedItem() >= count) {
            selectedItem.set(count - 1);
        }
    };

    function selectItem(index) {
        return function() {
            selectedItem.set(index);
            requestUseItem(index);
        }
    }

    function selectTab(index) {
        return function() {
            selectedItem.set(0);
            selectedTab.set(index);
        }
    }

    const useItem = (index) => {
        const item = filteredItems()[index];
        if (!item || item.type !== "item" || !item.usable) return;
        if (onInteraction) onInteraction("useItem", { id: item.id });
    };

    const requestUseItem = (index) => {
        const item = filteredItems()[index];
        if (!item || item.type !== "item" || !item.usable) return;
        if (item.consumable) {
            confirmItem.set(item);
            confirmOpen.set(true);
            selectedConfirm.set(0);
            return;
        }
        useItem(index);
    };

    const confirmUse = () => {
        const item = confirmItem();
        if (!item) return;
        delay(() => {
            if (onInteraction) onInteraction("useItem", { id: item.id });
            confirmOpen.set(false);
            confirmItem.set(null);
        });
    };

    const cancelConfirm = () => {
        delay(() => {
            confirmOpen.set(false);
            confirmItem.set(null);
        });
    };

    const openAssignment = () => {
        const item = confirmItem();
        if (!item?.hotbarAssignable || !canAssignItems()) return;
        confirmOpen.set(false);
        confirmItem.set(null);
        onAssignHotbar?.({ type: "item", id: item.id, name: item.name });
    };

    const stopEvent = (event) => {
        event?.stopPropagation?.();
    };

    const cancelConfirmFromPointer = (event) => {
        stopEvent(event);
        cancelConfirm();
    };

    function confirmSelect(index) {
        return function() {
            selectedConfirm.set(index);
            if (confirmOptions()[index]?.id === "use") {
                confirmUse();
                return;
            }
            if (confirmOptions()[index]?.id === "assign") {
                openAssignment();
                return;
            }
            cancelConfirm();
        }
    }

    const confirmControls = signal({
        left: {
            repeat: true,
            bind: keyboardControls.left,
            throttle: 150,
            keyDown() {
                if (!confirmOpen()) return;
                navConfirm.next(-1);
            }
        },
        right: {
            repeat: true,
            bind: keyboardControls.right,
            throttle: 150,
            keyDown() {
                if (!confirmOpen()) return;
                navConfirm.next(1);
            }
        },
        action: {
            bind: getKeyboardControlBind(keyboardControls.action),
            keyDown() {
                if (!confirmOpen()) return;
                confirmSelect(selectedConfirm())();
            }
        },
        escape: {
            bind: keyboardControls.escape,
            keyDown() {
                if (!confirmOpen()) return;
                cancelConfirm();
            }
        },
        gamepad: menuGamepadControls
    });

    const controls = signal({
        up: {
            repeat: true,
            bind: keyboardControls.up,
            throttle: 150,
            keyDown() {
                if (confirmOpen() || isAssignmentOpen()) {
                    return;
                }
                nav.next(-1);
            }
        },
        left: {
            repeat: true,
            bind: keyboardControls.left,
            throttle: 150,
            keyDown() {
                if (confirmOpen() || isAssignmentOpen()) {
                    return;
                }
                selectedTab.update((value) => (value - 1 + tabs.length) % tabs.length);
                selectedItem.set(0);
            }
        },
        right: {
            repeat: true,
            bind: keyboardControls.right,
            throttle: 150,
            keyDown() {
                if (confirmOpen() || isAssignmentOpen()) {
                    return;
                }
                selectedTab.update((value) => (value + 1) % tabs.length);
                selectedItem.set(0);
            }
        },
        down: {
            repeat: true,
            bind: keyboardControls.down,
            throttle: 150,
            keyDown() {
                if (confirmOpen() || isAssignmentOpen()) {
                    return;
                }
                nav.next(1);
            }
        },
        action: {
            bind: getKeyboardControlBind(keyboardControls.action),
            keyDown() {
                // The confirmation popup has its own controls (keyboard and gamepad).
                if (isAssignmentOpen() || confirmOpen()) return;
                requestUseItem(selectedItem());
            }
        },
        escape: {
            bind: keyboardControls.escape,
            keyDown() {
                if (isAssignmentOpen() || confirmOpen()) return;
                if (onBack) onBack();
            }
        },
        gamepad: menuGamepadControls
    });

    clampSelection();
</script>
